import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Markdown, LANG_LABEL } from "../components/common";

const BASE = process.env.REACT_APP_BACKEND_URL;
const LANGS = ["es", "fr", "en", "ru"];
const COLLECTION_LABEL = {
  scientific_article: "Actualités scientifiques", news: "Actualités scientifiques",
  patient_guide: "Guides patients", faq: "FAQ", medico_responde: "El Médico Responde",
  treatment_sheet: "Fiches traitements", molecule_sheet: "Fiches molécules", blog: "Blog",
};

export default function PublicPreview() {
  const { id } = useParams();
  const [sp, setSp] = useSearchParams();
  const lang = sp.get("lang") || "es";
  const [a, setA] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    setA(null); setErr(false);
    fetch(`${BASE}/api/public/content/${id}?lang=${lang}`)
      .then((r) => { if (!r.ok) throw new Error(); return r.json(); })
      .then((d) => { setA(d); if (d.meta_title) document.title = d.meta_title; })
      .catch(() => setErr(true));
  }, [id, lang]);

  useEffect(() => {
    if (!a?.jsonld) return;
    const s = document.createElement("script");
    s.type = "application/ld+json";
    s.text = JSON.stringify(a.jsonld);
    document.head.appendChild(s);
    return () => { try { document.head.removeChild(s); } catch {} };
  }, [a]);

  return (
    <div style={{ background: "#F7F5F0", minHeight: "100vh" }}>
      {/* Preview banner */}
      <div data-testid="preview-banner" style={{ background: "#0B3B2D", color: "#fff", padding: "10px 20px", fontSize: 13 }}>
        Aperçu rendu par le moteur éditorial · cet article s'affichera à l'identique sur <b>clinicavalorian.es</b> une fois le site branché au feed.
      </div>

      <div style={{ maxWidth: 760, margin: "0 auto", padding: "32px 20px 80px" }}>
        {err && <p style={{ color: "#b91c1c" }}>Article introuvable ou pas encore publié dans cette langue.</p>}
        {!a && !err && <p style={{ color: "#6b7280" }}>Chargement…</p>}
        {a && (
          <article data-testid="preview-article">
            {/* Lang switcher */}
            <div style={{ display: "flex", gap: 6, marginBottom: 18 }}>
              {LANGS.map((l) => (
                <button key={l} data-testid={`lang-${l}`} onClick={() => setSp({ lang: l })}
                  style={{ fontSize: 11, textTransform: "uppercase", padding: "4px 10px", borderRadius: 6,
                    border: "1px solid " + (l === lang ? "#0B3B2D" : "#d6d3cc"),
                    background: l === lang ? "#0B3B2D" : "#fff", color: l === lang ? "#fff" : "#5b6167", cursor: "pointer" }}>
                  {l}
                </button>
              ))}
            </div>

            <div style={{ fontSize: 12, letterSpacing: 1, textTransform: "uppercase", color: "#8a8f94" }}>
              {COLLECTION_LABEL[a.type] || a.type}
            </div>
            <h1 style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontSize: 40, lineHeight: 1.1, color: "#0B3B2D", margin: "8px 0 12px" }}>
              {a.title}
            </h1>
            {a.summary && <p style={{ fontSize: 18, color: "#4b5563", lineHeight: 1.6 }}>{a.summary}</p>}

            {(a.references?.length || a.sources?.length) > 0 && (
              <div data-testid="trust-badge" style={{ display: "inline-flex", alignItems: "center", gap: 8, marginTop: 16,
                background: "#0B3B2D", color: "#fff", borderRadius: 999, padding: "7px 14px", fontSize: 13, fontWeight: 600 }}>
                <span style={{ fontSize: 14 }}>✔</span>
                Vérifié par le Dr Florian A. Vallecillo Cabrera · {(a.references || a.sources).length} source{(a.references || a.sources).length > 1 ? "s" : ""}
              </div>
            )}

            {(a.references?.length || a.sources?.length) > 0 && (
              <div data-testid="sources-box-top" style={{ marginTop: 20, background: "#EDF3EF", border: "1px solid #cfe0d6",
                borderRadius: 12, padding: "16px 18px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10, flexWrap: "wrap" }}>
                  <span style={{ fontSize: 18 }}>🔬</span>
                  <span style={{ fontSize: 13, fontWeight: 700, color: "#0B3B2D" }}>
                    Article fondé sur {(a.references || a.sources).length} source{(a.references || a.sources).length > 1 ? "s" : ""} scientifique{(a.references || a.sources).length > 1 ? "s" : ""} révisée{(a.references || a.sources).length > 1 ? "s" : ""} par les pairs
                  </span>
                  {a.evidence_level?.label && (
                    <span data-testid="evidence-level-badge" style={{ fontSize: 11, fontWeight: 700, color: "#fff",
                      background: "#0B3B2D", borderRadius: 999, padding: "3px 10px" }}>
                      Niveau de preuve : {a.evidence_level.label}
                    </span>
                  )}
                </div>
                <ol style={{ margin: 0, paddingLeft: 20, color: "#1A2530" }}>
                  {(a.references || a.sources).map((s, i) => (
                    <li key={i} style={{ fontSize: 13, lineHeight: 1.5, marginBottom: 5 }}>
                      {s.authors ? `${s.authors}. ` : ""}{s.title}{s.journal ? ` — ${s.journal}` : ""}{s.year ? ` (${s.year})` : ""}{" "}
                      <a href={s.url} target="_blank" rel="noreferrer" style={{ color: "#2563EB", whiteSpace: "nowrap" }}>
                        {s.pmid ? `PMID ${s.pmid}` : "Voir la source"} ↗
                      </a>
                    </li>
                  ))}
                </ol>
              </div>
            )}

            <div style={{ marginTop: 24, fontSize: 16, lineHeight: 1.7, color: "#1A2530" }}>
              <Markdown text={a.body_markdown} />
            </div>

            {a.faq?.length > 0 && (
              <div style={{ marginTop: 36 }}>
                <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 26, color: "#0B3B2D" }}>Questions fréquentes</h2>
                {a.faq.map((f, i) => (
                  <div key={i} style={{ marginTop: 14 }}>
                    <div style={{ fontWeight: 600 }}>{f.q}</div>
                    <div style={{ color: "#4b5563", marginTop: 4 }}>{f.a}</div>
                  </div>
                ))}
              </div>
            )}
          </article>
        )}
      </div>
    </div>
  );
}
